import {
  Grid,
  HStack,
  Progress,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import ChecklistRtlSharpIcon from '@gamma/ui/icons/ChecklistRtlSharpIcon';
import DataAlertSharpIcon from '@gamma/ui/icons/DataAlertSharpIcon';
import PolicySharpIcon from '@gamma/ui/icons/PolicySharpIcon';
import TimelineSharpIcon from '@gamma/ui/icons/TimelineSharpIcon';
import { Panel, PanelHeading } from '@gamma/ui/layout';
import { i18n } from '../../../../../../shared/localization';
import { DetectionMethodSummaryProps } from './types';

interface DetectionMethodItemProps {
  methodKey: string;
  enabled: number;
  total: number;
}

const detectionMethods = {
  ml_results: {
    label: i18n.pages.alertCatalog.detectionMethods.machineLearning,
    Icon: undefined,
  },
  behavioral: {
    label: i18n.pages.alertCatalog.detectionMethods.behavioral,
    Icon: TimelineSharpIcon,
  },
  ids: {
    label: i18n.pages.alertCatalog.detectionMethods.ids,
    Icon: undefined,
  },
  anomaly: {
    label: i18n.pages.alertCatalog.detectionMethods.anomaly,
    Icon: DataAlertSharpIcon,
  },
  yara: {
    label: i18n.pages.alertCatalog.detectionMethods.yara,
    Icon: undefined,
  },
  notice: {
    label: i18n.pages.alertCatalog.detectionMethods.notice,
    Icon: undefined,
  },
  signature: {
    label: i18n.pages.alertCatalog.detectionMethods.signature,
    Icon: ChecklistRtlSharpIcon,
  },
  intel: {
    label: i18n.pages.alertCatalog.detectionMethods.intel,
    Icon: PolicySharpIcon,
  },
} satisfies Record<
  string,
  { label: string; Icon: typeof TimelineSharpIcon | undefined }
>;

const DetectionMethodItem = ({
  methodKey,
  enabled,
  total,
}: DetectionMethodItemProps) => {
  const { label, Icon } = (methodKey in detectionMethods
    ? detectionMethods[methodKey as keyof typeof detectionMethods]
    : undefined) ?? {
    label: methodKey,
    Icon: undefined,
  };
  const percentage = (enabled / total) * 100;

  return (
    <VStack align="flex-start" spacing={2} paddingTop="8px">
      <HStack spacing={2}>
        {Icon && <Icon />}
        <Text textStyle="body-md" fontWeight="medium">
          {label}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={1} width="100%">
        <Text textStyle="body-md">
          <Text as="span" color="text.primary">
            {enabled}
            {i18n.pages.alertCatalog.detectionMethods.of}
            {total}
          </Text>{' '}
          <Text as="span" color="text.secondary">
            {i18n.pages.alertCatalog.detectionMethods.enabled}
          </Text>
        </Text>
        <Progress
          value={percentage}
          size="sm"
          width="100%"
          height="var(--chakra-sizes-1)"
          borderRadius="full"
          bg={useColorModeValue('gray.500', 'gray.300')}
          sx={{
            '& > div': {
              backgroundColor: 'green.400',
            },
          }}
        />
      </VStack>
    </VStack>
  );
};

export const DetectionMethodSummary = ({
  data,
  loading,
}: DetectionMethodSummaryProps) => {
  if (loading || !data) {
    return null;
  }

  return (
    <Panel p={3}>
      <VStack align="stretch" spacing={4} paddingBottom={4}>
        <PanelHeading>
          <Text>{i18n.pages.alertCatalog.detectionMethods.title}</Text>
        </PanelHeading>
        <Grid templateColumns="repeat(4, 1fr)" gap={4}>
          {data.map((method) => (
            <DetectionMethodItem
              key={method.detection_method}
              methodKey={method.detection_method}
              enabled={method.enabled}
              total={method.total}
            />
          ))}
        </Grid>
      </VStack>
    </Panel>
  );
};
